import { useEffect, useState } from 'react';

import { useQuery } from '@hooks/reactQuery';
import { useQueryUser } from '@hooks/useQueryUser';

import { getOrganisationCurrentUsers } from '@api/organisations';
import { getUserActions, getUserLog } from '@api/userLog';

import { extractQueryParams } from '@utils/common';
import { actionsFormatted, usersFormatted } from '@utils/formatter';

import { TNameTitle, TNameTitleValueLabel } from '@interfaces/common/common';
import { IGetUserLogDTO } from '@interfaces/user/dto/userDTO';
import { IUserEntity } from '@interfaces/user/user';

import {
	ORGANIZATIONS_USERS,
	USER_ACTIONS_FILTER,
	USERS_ACTIONS,
} from '@constants/queryKeys';

interface IUseUserHistoryParams extends IGetUserLogDTO {
	isAssetOwnerOrganizationAdministrator?: boolean;
	isNetworkOperatorOrganizationAdministrator?: boolean;
	isAssetConsumerOrganizationAdministrator?: boolean;
}

export const useUserHistory = (params?: IUseUserHistoryParams) => {
	const [isGetOrganizationsUsers, setIsGetOrganizationsUsers] = useState(false);

	const { organisationId: organizationId } = useQueryUser();

	const {
		isAssetOwnerOrganizationAdministrator = false,
		isNetworkOperatorOrganizationAdministrator = false,
		isAssetConsumerOrganizationAdministrator = false,
		organizationUser,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		createdAtFrom,
		createdAtTo,
		userAction,
	} = params || {};

	const {
		data,
		isLoading: isLoadingUserActions,
		isFetching: isFetchingUserActions,
	} = useQuery({
		queryKey: [
			USER_ACTIONS_FILTER,
			{
				organizationUser,
				page,
				pageSize,
				sortDirection,
				sortFieldName,
				createdAtFrom,
				createdAtTo,
				userAction,
			},
		],
		queryFn: extractQueryParams(getUserLog),
		refetchOnWindowFocus: false,
	});

	const {
		data: organizationsUsersData,
		isLoading: isLoadingOrganizationsUsersData,
		isFetching: isFetchingOrganizationsUsersData,
	} = useQuery<IUserEntity[], TNameTitleValueLabel[]>({
		queryKey: [ORGANIZATIONS_USERS, { organizationId }],
		queryFn: extractQueryParams(getOrganisationCurrentUsers),
		enabled: [isGetOrganizationsUsers, !!organizationId],
		select: (organizationsUsersData: IUserEntity[]) => usersFormatted(organizationsUsersData),
		refetchOnWindowFocus: false,
	});

	const {
		data: userActionsData,
		isLoading: isLoadingUserActionsData,
		isFetching: isFetchingUserActionsData,
	} = useQuery({
		queryKey: [USERS_ACTIONS],
		queryFn: getUserActions,
		select: (organizationsUsersData: Array<TNameTitle>) => (
			actionsFormatted(organizationsUsersData)
		),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		setIsGetOrganizationsUsers((isAssetOwnerOrganizationAdministrator
				|| isNetworkOperatorOrganizationAdministrator || isAssetOwnerOrganizationAdministrator)
			&& !!data);
	}, [data,
		isAssetOwnerOrganizationAdministrator,
		isNetworkOperatorOrganizationAdministrator,
		isAssetConsumerOrganizationAdministrator,
	]);

	return {
		organizationsUsersData: organizationsUsersData || [],
		countOfRecords: data?.count || 0,
		userHistoryByFilter: data?.items || [],
		isLoading: (isLoadingUserActions && isFetchingUserActions)
			|| (isLoadingOrganizationsUsersData && isFetchingOrganizationsUsersData)
			|| (isLoadingUserActionsData && isFetchingUserActionsData),
		userActionsData: userActionsData || [],
	};
};
